/**
 * Component to preload critical default images for better performance
 * This ensures local default images are cached and ready for instant display
 */

"use client";

import { useEffect } from "react";
import {
  getPreloadImages,
  getAvailableContentTypes,
} from "../utils/imageUtils";

export default function ImagePreloader() {
  useEffect(() => {
    // Preload critical default images
    const preloadImages = getPreloadImages([
      "blog",
      "event",
      "gallery",
      "press-release",
      "media-coverage",
    ]);

    preloadImages.forEach((imageSrc) => {
      const link = document.createElement("link");
      link.rel = "preload";
      link.as = "image";
      link.href = imageSrc;
      link.crossOrigin = "anonymous";
      document.head.appendChild(link);
    });

    // Cleanup function to remove preload links when component unmounts
    return () => {
      const preloadLinks = document.querySelectorAll(
        'link[rel="preload"][as="image"]'
      );
      preloadLinks.forEach((link) => {
        if (preloadImages.includes(link.href)) {
          link.remove();
        }
      });
    };
  }, []);

  return null; // This component doesn't render anything
}

/**
 * Hook to preload images for specific content types
 * @param {Array} contentTypes - Array of content types to preload
 */
export function useImagePreloader(contentTypes = []) {
  useEffect(() => {
    if (contentTypes.length === 0) return;

    const preloadImages = getPreloadImages(contentTypes);

    preloadImages.forEach((imageSrc) => {
      const img = new Image();
      img.src = imageSrc;
    });
  }, [contentTypes]);
}

/**
 * Utility function to preload all default images
 * Call this in your app's main layout or _app.js for maximum performance
 */
export function preloadAllDefaultImages() {
  const allContentTypes = getAvailableContentTypes();
  const preloadImages = getPreloadImages(allContentTypes);

  preloadImages.forEach((imageSrc) => {
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = imageSrc;
    link.crossOrigin = "anonymous";
    document.head.appendChild(link);
  });
}
